import { useCallback, useEffect, useMemo, useState } from "react"
import { ServiceCategory } from "../../models/BdaService";
import { useToasts } from "../../contexts/toasts";
import { useFullScreenLoader } from "../../contexts/fullScreenLoader";
import { Badge, Breadcrumb, Button, Col, Container, Modal, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Alert, Flex, Space, Table, FormProps, Form, Input } from "antd";
import AppSearch from "../../components/common/AppSearch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import IconButton from "../../components/common/IconButton";
import { Categories } from "../../api/Categories";
import AppLoader from "../../components/common/AppLoader";

const formLayout: FormProps = {
  layout: "horizontal",
  colon: false,
}

export default function CategoriesManagement() {
  const [form] = Form.useForm();
  const [error, setError] = useState(false);
  const [categories, setCategories] = useState<ServiceCategory[]>();
  const [selectedCategories, setSelectedCategories] = useState<ServiceCategory[]>([]);
  const [category, setCategory] = useState<ServiceCategory>();
  const [searchText, setSearchText] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const getCategories = useCallback(() => {
    const promise = selectedCategories.length === 0
      ? Categories.getCategories()
      : Categories.getCategory(selectedCategories[selectedCategories.length - 1].id!);

    promise
      .then(
        ({ data }) => {
          if (selectedCategories.length === 0) setCategories(data as ServiceCategory[]);
          else setCategories((data as ServiceCategory).children);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('categories-error', message, `Categories Error ${status ? `(${status})` : ''}`, 'danger');
          setError(true);
        }
      );
  }, [showToast, selectedCategories]);

  const reset = useCallback(() => {
    setError(false);
    setSearchText("");
    setCategories(undefined);
    getCategories();
  }, [getCategories]);

  useEffect(reset, [reset, selectedCategories]);

  const handleSelectCategory = useCallback((category: ServiceCategory) => () => {
    setSelectedCategories(curr => (curr || []).concat(category));
  }, []);

  const openCreateModal = useCallback(() => setShowEditModal(true), []);

  const handleEdit = useCallback((item: ServiceCategory) => () => {
    setCategory(item);
    form.setFieldsValue(item);
    setShowEditModal(true);
  }, [form]);
  const openDeleteModal = useCallback((item: ServiceCategory) => () => {
    setCategory(item);
    setShowDeleteModal(true);
  }, []);
  const handleDeleteModalClose = useCallback(() => {
    setCategory(undefined);
    setShowDeleteModal(false);
  }, []);
  const handleEditModalClose = useCallback(() => {
    setShowEditModal(false);
    setCategory(undefined);
    form.resetFields();
  }, [form]);

  const filteredCategories = useMemo(() => {
    return categories?.filter(category => category.name.toLowerCase().includes(searchText.toLowerCase())).map(c => ({ id: c.id, name: c.name }));
  }, [categories, searchText]);

  const handleSubmit = useCallback((values: any) => {
    showLoader();

    const body = {
      ...values,
      fatherId: selectedCategories[selectedCategories.length - 1]?.id || null
    }

    const promise = category !== undefined
      ? Categories.updateCategory(category!.id!, body)
      : Categories.createCategory(body);

    promise.then(
      () => {
        hideLoader();
        showToast('category-update-success', `Category ${category ? 'updated' : 'created'} successfully`, 'Category Success', 'success');
        handleEditModalClose();
        reset();
      },
      (err) => {
        hideLoader();
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('category-update-error', message, `Category Error ${status ? `(${status})` : ''}`, 'danger');
      }
    )
  }, [category, showLoader, hideLoader, reset, showToast, selectedCategories, handleEditModalClose]);

  const deleteCategory = useCallback(() => {
    showLoader();

    Categories.deleteCategory(category!.id!)
      .then(
        () => {
          hideLoader();
          showToast('category-delete-success', 'Category deleted successfully', 'Category Delete Success', 'success');
          handleDeleteModalClose();
          reset();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('category-delete-error', message, `Category Delete Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [category, showLoader, hideLoader, showToast, reset, handleDeleteModalClose]);

  const handleRemoveCategory = useCallback((index: number) => () => {
    setSelectedCategories(curr => curr!.slice(0, index));
  }, []);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert
        type="error"
        showIcon
        message="An error occurred while retrieving Categories. Try again later."
      />
    )

    if (!filteredCategories) return <AppLoader className="py-5 my-5" justify="center" size={100} />;

    return (
      <Table
        size="middle"
        scroll={{ y: 'calc(100vh - 300px)' }}
        dataSource={filteredCategories}
        rowKey="id"
      >
        <Table.Column
          key="key"
          dataIndex="name"
          title="Name"
          width="30%"
          render={(_, record: ServiceCategory) => (
            <Link to="#" onClick={handleSelectCategory(record)}>{record.name}</Link>
          )}
        />
        <Table.Column
          key="actions"
          title="Actions"
          className="text-center"
          width="10%"
          render={(_value, record: ServiceCategory) => (
            <Space size={5}>
              <IconButton tooltip="Edit" onClick={handleEdit(record)}>
                <FontAwesomeIcon fixedWidth icon={solid('pen')} />
              </IconButton>
              <IconButton tooltip="Delete" className="text-danger" onClick={openDeleteModal(record)}>
                <FontAwesomeIcon fixedWidth icon={solid('trash')} />
              </IconButton>
            </Space>
          )}
        />
      </Table>
    )
  }, [error, filteredCategories, handleEdit, openDeleteModal, handleSelectCategory]);

  return (
    <Container className="my-3">
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/control-panel" }}>Control Panel</Breadcrumb.Item>
        <Breadcrumb.Item active>Categories Management</Breadcrumb.Item>
      </Breadcrumb>

      <Row className="mb-3 align-items-center">
        <Col>
          <Flex align="center">
            <h5 className="mb-0">Categories</h5>
            {selectedCategories.map((category, index) => (
              <>
                <FontAwesomeIcon icon={solid('chevron-right')} size="xs" className="mx-2" transform="down-2" />
                <Badge key={category.id!} bg="light" className="d-flex align-items-center border text-dark" pill>
                  <span className="me-2 pe-1">{category.name}</span>
                  <IconButton className="p-0" onClick={handleRemoveCategory(index)}>
                    <FontAwesomeIcon icon={solid('times')} />
                  </IconButton>
                </Badge>
              </>
            ))}
          </Flex>
        </Col>

        <Col className="text-center px-3">
          <AppSearch value={searchText} placeholder={`Search ${selectedCategories.length > 0 ? 'sub-' : ''}category...`} onChange={setSearchText} />
        </Col>

        <Col md={3} className="text-end">
          <Button size="sm" variant="success" onClick={openCreateModal}>
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="ms-2">Register {selectedCategories.length > 0 ? 'Sub-' : ''}Category</span>
          </Button>
        </Col>
      </Row>

      {renderAsyncContent()}

      <Modal show={showDeleteModal} onHide={handleDeleteModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete category <b>{category?.name}</b>?</p>

          <Alert type="warning" showIcon message="Remember, this operation cannot be undone" />
        </Modal.Body>
        <Modal.Footer>
          <Flex justify="space-between" className="mt-3">
            <Button size="sm" variant="light" className="border" onClick={handleDeleteModalClose}>Cancel</Button>
            <Button size="sm" className="ms-2" variant="danger" onClick={deleteCategory}>
              <FontAwesomeIcon icon={solid('trash')} />
              <span className="ms-2">Delete</span>
            </Button>
          </Flex>
        </Modal.Footer>
      </Modal>

      <Modal show={showEditModal} onHide={handleEditModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{`${category?.id ? 'Edit' : 'Create'} ${selectedCategories.length > 0 ? 'Sub-' : ''}Category`}</Modal.Title>
        </Modal.Header>
        <Form className="compact-form" form={form} {...formLayout} onFinish={handleSubmit}>
          <Modal.Body>
            <Form.Item name="name" label="Name">
              <Input placeholder="Category Name" />
            </Form.Item>
          </Modal.Body>
          <Modal.Footer className="justify-content-between">
            <Button type="button" variant="light" size="sm" className="border" onClick={handleEditModalClose}>Close</Button>
            <Button type="submit" variant="success" size="sm">
              <FontAwesomeIcon icon={solid('save')} />
              <span className="ms-2">Save</span>
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  )
}